import { Button, Form } from 'antd';
import { useCallback } from 'react';
import ScheduleCGUStep from 'components/schedule/ScheduleCGUStep';
import ScheduleDateStep from 'components/schedule/ScheduleDateStep';
import { ScheduleType } from 'components/schedule/ScheduleType';
import { useAppDispatch } from 'stores/hooks';
import { createMeetingAsync } from 'stores/meetings/meetingsSlice';
import { FormInstance } from 'antd/lib/form/hooks/useForm';
import { MEETING_TYPES } from 'commons/types/meetingsTypes';
import { Patient } from 'commons/types/patientsTypes';

const SCHEDULE_STEPS = {
  date_choice: 'date',
  cgu: 'cgu',
};

interface SchedulePsadProps {
  patient: Patient | undefined;
  form: FormInstance<any>;
  defaultScheduledForm: ScheduleType;
  step: string;
  hideCGUs: boolean;
  onChangeStep: (nextStep: string) => () => void;
}

export default function SchedulePsad({
  patient,
  form,
  defaultScheduledForm,
  step,
  onChangeStep,
  hideCGUs,
}: SchedulePsadProps) {
  const nextRdvType = MEETING_TYPES.RDV_SWITCH;
  const dispatch = useAppDispatch();
  const onFinalize = useCallback(
    (schedule: ScheduleType) => {
      dispatch(
        createMeetingAsync({
          ...schedule,
          type: nextRdvType,
          serviceID: form.getFieldValue('serviceID') || '',
        }),
      );
    },
    [dispatch, form, nextRdvType],
  );

  return (
    <Form
      form={form}
      name='schedule-form'
      onFinish={onFinalize}
      initialValues={defaultScheduledForm}
      className='flex flex-col items-center sm:pt-12'
    >
      <RDVContent />
      <div
        className={step === SCHEDULE_STEPS.date_choice ? 'w-full' : 'hidden'}
      >
        <ScheduleDateStep
          patient={patient}
          onNext={onChangeStep(SCHEDULE_STEPS.cgu)}
          form={form}
          nextRdvType={nextRdvType}
          scroll
        />
      </div>
      <div className={step === SCHEDULE_STEPS.cgu ? 'w-full' : 'hidden'}>
        <Button
          onClick={onChangeStep(SCHEDULE_STEPS.date_choice)}
          type='text'
        >
          Retour
        </Button>
        <ScheduleCGUStep
          form={form}
          hideCGUs={hideCGUs}
          nextRdvType={nextRdvType}
        />
      </div>
      <div className='pt-6'>
        <div className='grid max-w-md grid-cols-3 gap-4 mx-auto'>
          <div className='flex flex-col items-center justify-center flex-shrink-0'>
            <div className='text-4xl font-black text-secondary'>4,7</div>
            <div className='text-sm text-center sm:text-lg'>avis Google</div>
            <div className='flex items-center justify-center text-yellow-400 gap-x-1 h-[20px] sm:h-[24px] w-[104px]'>
              <Star />
              <Star />
              <Star />
              <Star />
              <Star />
            </div>
          </div>
          <div className='flex flex-col items-center justify-center flex-shrink-0'>
            <div className='text-4xl font-black text-secondary'>3000+</div>
            <div className='text-sm text-center sm:text-lg'>
              patients
              <br />
              diagnostiqués
            </div>
          </div>
          <div className='flex flex-col items-center justify-center flex-shrink-0'>
            <div className='text-4xl font-black text-secondary'>20</div>
            <div className='text-sm text-center sm:text-lg'>
              médecins
              <br />
              du sommeil
            </div>
          </div>
        </div>
      </div>
      <div className='pt-6'>
        <div className='grid max-w-md grid-cols-4 gap-4 mx-auto'>
          <div className='flex flex-col items-center justify-center flex-shrink-0 h-[50px]'>
            <img
              className='object-contain aspect-[120/50]'
              src='/BFM.png'
              alt='BFMTV'
            />
          </div>
          <div className='flex flex-col items-center justify-center flex-shrink-0 h-[50px]'>
            <img
              className='object-contain'
              src='/LeMonde.png'
              alt='Le Monde'
            />
          </div>
          <div className='flex flex-col items-center justify-center flex-shrink-0 h-[50px]'>
            <img
              className='object-contain'
              src='/Maddyness.png'
              alt='Maddyness'
            />
          </div>
          <div className='flex flex-col items-center justify-center flex-shrink-0 h-[50px]'>
            <img
              className='object-contain'
              src='/LeFigaro.png'
              alt='Le Figaro'
            />
          </div>
        </div>
      </div>
    </Form>
  );
}

function RDVContent() {
  return (
    <div className='px-4 pb-6 text-center'>
      <h1 className='mb-8 text-2xl font-bold'>
        Faites le point sur votre traitement
      </h1>
      <p className='m-0 text-xl'>
        Vous avez du mal à supporter votre appareil ? Le suivi apporté par votre
        prestataire n&apos;est pas suffisant? Parlez en gratuitement avec notre
        infirmier référent qui vous présentera les différentes solutions
        possibles.
      </p>
    </div>
  );
}

function Star() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      viewBox='0 0 32 32'
      width='32px'
      height='32px'
    >
      <path
        d='M31.77 11.857H19.74L15.99.5l-3.782 11.357H0l9.885 6.903-3.692 11.21 9.736-7.05 9.796 6.962-3.722-11.18 9.766-6.845z'
        fill='currentColor'
      />
    </svg>
  );
}
